import { useNavigate, useRouter } from "@tanstack/react-router";

import { type OrionRoutePath } from "./OrionBreadcrumbs";
import { MuiButton } from "../MuiComponents/MuiButton";
import type { ButtonProps } from "@mui/material";
import type { ReactNode } from "react";

import { ArrowBackIcon } from "@/icons";

export type OrionBackButtonProps = {
  /**
   * Safe destination used when there is no previous in-app screen to return to
   * (e.g. the page was deep-linked or opened in a new tab). By default the
   * button goes back in history; `to` is only the fallback for that case. Set
   * it to the page's hierarchical parent.
   */
  to?: OrionRoutePath;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
  /**
   * Custom back handler; takes precedence over everything. Use for flows that
   * need bespoke logic on back (e.g. unsaved-changes guards). When omitted, the
   * button goes back in history, falling back to `to`/`fallbackTo`.
   */
  onBack?: () => void;
  /**
   * Alternative safe destination for the history fallback (deep links / new
   * tabs). Equivalent to `to`; used when `to` is not set.
   */
  fallbackTo?: OrionRoutePath;
  fallbackParams?: Record<string, string>;
  /** Button label. Defaults to "GO BACK" (uppercase, matching solid buttons). */
  label?: ReactNode;
  /** Accessible name. Defaults to the visible label; set to override it. */
  "aria-label"?: string;
  variant?: ButtonProps["variant"];
  color?: ButtonProps["color"];
  disabled?: boolean;
  /** Set to false to hide the leading arrow icon. */
  showIcon?: boolean;
  sx?: ButtonProps["sx"];
  "data-testid"?: string;
};

/**
 * Standardized "GO BACK" button. Consistent label, icon (ArrowBack), and
 * placement across the platform. Navigation is resolved in priority order:
 * `onBack` → previous in-app screen (history) → `to` → `fallbackTo`.
 *
 * Going back in history is the default so "GO BACK" returns the user to
 * wherever they actually came from; `to`/`fallbackTo` are only used when there
 * is no in-app history (deep link / new tab).
 */
export function OrionBackButton({
  to,
  params,
  search,
  onBack,
  fallbackTo,
  fallbackParams,
  label = "GO BACK",
  "aria-label": ariaLabel,
  variant = "text",
  color = "primary",
  disabled,
  showIcon = true,
  sx,
  "data-testid": dataTestId,
}: OrionBackButtonProps) {
  const navigate = useNavigate();
  const router = useRouter();

  const handleClick = () => {
    // A custom handler always wins (e.g. unsaved-changes guards).
    if (onBack) {
      onBack();
      return;
    }
    // Default: return to the previous in-app screen when one exists.
    // `router.history.canGoBack()` reflects the router's own session-scoped
    // history index, so (unlike `window.history.length`) it is not inflated by
    // external referrers — it's false on deep links / new tabs, where a plain
    // `back()` would navigate off-app. In that case fall back to the explicit
    // destination (`to`, else `fallbackTo`).
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    if (to) {
      void navigate({ to, params, search } as Parameters<typeof navigate>[0]);
      return;
    }
    if (fallbackTo) {
      void navigate({
        to: fallbackTo,
        params: fallbackParams,
      } as Parameters<typeof navigate>[0]);
    }
  };

  return (
    <MuiButton
      variant={variant}
      color={color}
      disabled={disabled}
      startIcon={showIcon ? <ArrowBackIcon /> : undefined}
      onClick={handleClick}
      sx={sx}
      data-testid={dataTestId}
      aria-label={ariaLabel}
    >
      {label}
    </MuiButton>
  );
}
